import React from 'react';
import {
  TextInput,
  Edit,
  BooleanInput,
  SimpleForm,
  useRecordContext,
  useGetOne,
  useGetList,
  minValue,
  required,
} from 'react-admin';
import EnumSelectInput from 'components/Enum/EnumSelectInput';
import PositionInput from 'components/PositionInput';
import SpecialtyInput from 'components/SpecialtyInput';
import MoneyInput from 'components/MoneyInput';

const useRateValidation = (locationId, positionIds, specialtyIds) => {
  const { data: rateSettings, isLoading } = useGetList(
    'LocationPositionRateSetting',
    {
      filter: {
        max_only: true,
        include_parents: true,
        location_id: locationId,
        search: {
          position_ids: positionIds,
          specialty_ids: specialtyIds,
        },
      },
    },
    { enabled: !!(locationId && positionIds?.length) },
  );

  const rateSetting = rateSettings?.[0];
  const minRate = rateSetting?.settings?.ten99?.charge_rate_cents || 0;
  const suggestedRate = minRate ? Math.floor(minRate * 1.12) : 0;

  const validateRate = (value) => {
    if (!minRate) {
      return undefined;
    }
    if (!value || value < minRate) {
      return `Rate must be at least $${(minRate / 100).toFixed(2)} (suggested: $${(
        suggestedRate / 100
      ).toFixed(2)})`;
    }
    return undefined;
  };

  return {
    minRate,
    suggestedRate,
    validateRate,
    isLoading,
  };
};

const ConditionalRateField = () => {
  const record = useRecordContext();
  const locationId = record?.location_id;
  const positionIds = record?.position_ids;
  const specialtyIds = record?.specialty_ids;

  const { data: location, isLoading: locationLoading } = useGetOne(
    'Location',
    { id: locationId },
    { enabled: !!locationId },
  );
  const { data: company, isLoading: companyLoading } = useGetOne(
    'Company',
    { id: location?.company_id },
    { enabled: !!location?.company_id },
  );

  const {
    minRate,
    suggestedRate,
    validateRate,
    isLoading: rateValidationLoading,
  } = useRateValidation(locationId, positionIds, specialtyIds);

  if (locationLoading || companyLoading || rateValidationLoading) {
    return null;
  }

  if (!location || !company) {
    return null;
  }

  if (company.enable_template_rate) {
    const helperText = minRate
      ? `Minimum: $${(minRate / 100).toFixed(2)}, Suggested: $${(suggestedRate / 100).toFixed(2)}`
      : undefined;

    return (
      <MoneyInput
        source="charge_base_hourly_rate_cents"
        label="Rate"
        validate={[minValue(1, 'Rate is required'), validateRate]}
        helperText={helperText}
      />
    );
  }

  return null;
};

const ConditionalPositionInput = () => {
  const record = useRecordContext();
  const hasRate = record?.charge_base_hourly_rate_cents && record.charge_base_hourly_rate_cents > 0;

  return (
    <PositionInput
      multiple
      filter={{ active: true }}
      disabled={hasRate}
      helperText={hasRate ? 'Cannot edit positions when rate is set' : undefined}
    />
  );
};

const ConditionalSpecialtyInput = () => {
  const record = useRecordContext();
  const hasRate = record?.charge_base_hourly_rate_cents && record.charge_base_hourly_rate_cents > 0;

  return (
    <SpecialtyInput
      multiple
      positionSource="position_ids"
      optionText="name"
      disabled={hasRate}
      helperText={hasRate ? 'Cannot edit specialties when rate is set' : undefined}
    />
  );
};

const JobTemplateEdit = () => (
  <Edit mutationMode="pessimistic" redirect="show">
    <SimpleForm>
      <EnumSelectInput source="kind" />
      <ConditionalPositionInput />
      <ConditionalSpecialtyInput />
      <EnumSelectInput source="patient_population" />
      <BooleanInput source="covid_care" />
      <TextInput multiline fullWidth source="detail" />
      <TextInput source="name" validate={[required()]} />
      <ConditionalRateField />
    </SimpleForm>
  </Edit>
);

export default JobTemplateEdit;
